import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

const CuponsTab = ({ currentUserRole }) => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    const [cupons, setCupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [formData, setFormData] = useState({
        codigo: '', valor_desconto: '', tipo_desconto: 'valor_fixo',
        usos_maximos: '', data_validade: '', ativo: true,
    });
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const canManageCupons = currentUserRole === 'Representante Legal' || currentUserRole === 'Gerente';

    const fetchCupons = useCallback(async () => {
        if (!currentEntidade?.id) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/api/cupons?entidade_id=${currentEntidade.id}`);
            setCupons(response.data.cupons);
        } catch (err) {
            setError('Não foi possível carregar os cupons.');
            showAlertModal('Erro', 'Não foi possível carregar os cupons.', 'danger');
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, showAlertModal]);

    useEffect(() => { fetchCupons(); }, [fetchCupons]);

    const handleShowModal = (coupon = null) => {
        setEditingCoupon(coupon);
        if (coupon) {
            setFormData({
                codigo: coupon.codigo,
                valor_desconto: coupon.valor_desconto,
                tipo_desconto: coupon.tipo_desconto,
                usos_maximos: coupon.usos_maximos === -1 ? '' : coupon.usos_maximos,
                data_validade: coupon.data_validade ? coupon.data_validade.split('T')[0] : '',
                ativo: coupon.ativo,
            });
        } else {
            setFormData({ codigo: '', valor_desconto: '', tipo_desconto: 'valor_fixo', usos_maximos: '', data_validade: '', ativo: true });
        }
        setShowModal(true);
        setTimeout(() => setIsModalVisible(true), 10);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setTimeout(() => { setShowModal(false); setEditingCoupon(null); }, 300);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveCoupon = async () => {
        if (!canManageCupons) { showAlertModal('Acesso Negado', 'Você não tem permissão para gerenciar cupons.', 'danger'); return; }
        if (!formData.codigo || !formData.valor_desconto || !formData.tipo_desconto) {
            showAlertModal('Validação', 'Código, Valor de Desconto e Tipo são obrigatórios.', 'warning'); return;
        }
        setIsSaving(true);
        try {
            const dataToSave = {
                ...formData,
                entidade_id: currentEntidade.id,
                usos_maximos: formData.usos_maximos === '' ? -1 : parseInt(formData.usos_maximos, 10),
                valor_desconto: parseFloat(formData.valor_desconto),
            };
            if (editingCoupon) {
                await axios.put(`/api/cupons/${editingCoupon.id}`, dataToSave);
                showAlertModal('Sucesso', 'Cupom atualizado com sucesso!', 'success');
            } else {
                await axios.post('/api/cupons', dataToSave);
                showAlertModal('Sucesso', 'Cupom criado com sucesso!', 'success');
            }
            fetchCupons();
            handleCloseModal();
        } catch (err) {
            showAlertModal('Erro', err.response?.data?.message || 'Erro ao salvar cupom.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCoupon = (coupon) => {
        if (!canManageCupons) { showAlertModal('Acesso Negado', 'Você não tem permissão.', 'danger'); return; }
        setCouponToDelete(coupon);
        setShowConfirmDeleteModal(true);
        setTimeout(() => setIsModalVisible(true), 10);
    };

    const handleConfirmDelete = async () => {
        if (!couponToDelete) return;
        setIsSaving(true);
        try {
            await axios.delete(`/api/cupons/${couponToDelete.id}`);
            showAlertModal('Sucesso', 'Cupom excluído com sucesso!', 'success');
            fetchCupons();
            setIsModalVisible(false);
            setTimeout(() => { setShowConfirmDeleteModal(false); setCouponToDelete(null); }, 300);
        } catch (err) {
            showAlertModal('Erro', err.response?.data?.message || 'Erro ao excluir cupom.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleStatus = async (coupon) => {
        if (!canManageCupons) { showAlertModal('Acesso Negado', 'Você não tem permissão.', 'danger'); return; }
        try {
            await axios.patch(`/api/cupons/${coupon.id}/toggle-status`);
            showAlertModal('Sucesso', `Cupom ${coupon.ativo ? 'inativado' : 'ativado'} com sucesso!`, 'success');
            fetchCupons();
        } catch (err) {
            showAlertModal('Erro', err.response?.data?.message || 'Erro ao alterar status.', 'danger');
        }
    };

    if (isLoading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '12px' }}>
            <Spinner animation="border" variant="primary" size="sm" />
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>Carregando cupons...</span>
        </div>
    );

    if (error) return (
        <div style={{ padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#dc2626', fontSize: '14px' }}>
            <FontAwesomeIcon icon="exclamation-circle" style={{ marginRight: '8px' }} />{error}
        </div>
    );

    return (
        <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

            {/* Header da aba */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                        Gestão de Cupons
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9ca3af' }}>
                        {cupons.length} cupom{cupons.length !== 1 ? 's' : ''} cadastrado{cupons.length !== 1 ? 's' : ''}
                    </p>
                </div>
                {canManageCupons && (
                    <button
                        onClick={() => handleShowModal()}
                        style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: '#fff', border: 'none', borderRadius: '8px',
                            cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            boxShadow: '0 1px 3px rgba(59,130,246,0.4)',
                        }}
                    >
                        <FontAwesomeIcon icon="plus" />
                        Novo Cupom
                    </button>
                )}
            </div>

            {/* Lista de cupons */}
            {cupons.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '60px 24px',
                    backgroundColor: '#f9fafb', borderRadius: '12px',
                    border: '2px dashed #e5e7eb',
                }}>
                    <div style={{
                        width: '64px', height: '64px', borderRadius: '16px',
                        backgroundColor: '#f3f4f6', margin: '0 auto 16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FontAwesomeIcon icon="tag" style={{ fontSize: '28px', color: '#d1d5db' }} />
                    </div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                        Nenhum cupom cadastrado
                    </h3>
                    <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#9ca3af' }}>
                        Crie cupons de desconto para suas campanhas de marketing
                    </p>
                    {canManageCupons && (
                        <button
                            onClick={() => handleShowModal()}
                            style={{
                                padding: '8px 20px',
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                color: '#fff', border: 'none', borderRadius: '8px',
                                cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            }}
                        >
                            <FontAwesomeIcon icon="plus" style={{ marginRight: '6px' }} />
                            Criar primeiro cupom
                        </button>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {cupons.map(coupon => (
                        <CouponCard
                            key={coupon.id}
                            coupon={coupon}
                            canManage={canManageCupons}
                            onEdit={() => handleShowModal(coupon)}
                            onToggle={() => handleToggleStatus(coupon)}
                            onDelete={() => handleDeleteCoupon(coupon)}
                        />
                    ))}
                </div>
            )}

            {/* Modal Criar/Editar */}
            {showModal && (
                <>
                    <div
                        onClick={handleCloseModal}
                        style={{
                            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
                            zIndex: 1040, opacity: isModalVisible ? 1 : 0, transition: 'opacity 0.3s',
                        }}
                    />
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: `translate(-50%, ${isModalVisible ? '-50%' : '-45%'})`,
                        width: '100%', maxWidth: '520px',
                        backgroundColor: '#fff', borderRadius: '16px',
                        zIndex: 1050, padding: '28px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        opacity: isModalVisible ? 1 : 0,
                        transition: 'all 0.3s ease',
                    }}>
                        {/* Header do modal */}
                        <div style={{ display: 'flex',Items: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon="tag" style={{ color: '#fff', fontSize: '14px' }} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                                    {editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
                                </h3>
                            </div>
                            <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px' }}>
                                <FontAwesomeIcon icon="times" />
                            </button>
                        </div>

                        {/* Formulário */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Código */}
                            <div>
                                <label style={labelStyle}>Código do Cupom *</label>
                                <input
                                    name="codigo"
                                    value={formData.codigo}
                                    onChange={handleChange}
                                    placeholder="Ex: DESCONTO10"
                                    style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            {/* Tipo e Valor */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={labelStyle}>Tipo de Desconto *</label>
                                    <select
                                        name="tipo_desconto"
                                        value={formData.tipo_desconto}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    >
                                        <option value="valor_fixo">Valor Fixo (R$)</option>
                                        <option value="porcentagem">Porcentagem (%)</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Valor do Desconto *</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{
                                            position: 'absolute', left: '12px', top: '50%',
                                            transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '13px',
                                        }}>
                                            {formData.tipo_desconto === 'valor_fixo' ? 'R$' : '%'}
                                        </span>
                                        <input
                                            type="number"
                                            name="valor_desconto"
                                            value={formData.valor_desconto}
                                            onChange={handleChange}
                                            step="0.01" min="0"
                                            style={{ ...inputStyle, paddingLeft: '36px' }}
                                            onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Usos e Validade */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={labelStyle}>Usos Máximos</label>
                                    <input
                                        type="number"
                                        name="usos_maximos"
                                        value={formData.usos_maximos}
                                        onChange={handleChange}
                                        placeholder="Ilimitado"
                                        min="0"
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                                        Deixe em branco para ilimitado
                                    </p>
                                </div>
                                <div>
                                    <label style={labelStyle}>Data de Validade</label>
                                    <input
                                        type="date"
                                        name="data_validade"
                                        value={formData.data_validade}
                                        onChange={handleChange}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>
                            </div>

                            {/* Toggle Ativo */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Cupom Ativo</div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>Cupons inativos não podem ser utilizados</div>
                                </div>
                                <div
                                    onClick={() => setFormData(prev => ({ ...prev, ativo: !prev.ativo }))}
                                    style={{
                                        width: '44px', height: '24px', borderRadius: '999px',
                                        backgroundColor: formData.ativo ? '#3b82f6' : '#d1d5db',
                                        cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                        flexShrink: 0,
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: '2px',
                                        left: formData.ativo ? '22px' : '2px',
                                        width: '20px', height: '20px', borderRadius: '50%',
                                        backgroundColor: '#fff', transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }} />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleCloseModal}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px', borderRadius: '8px',
                                    backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb',
                                    color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveCoupon}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 20px', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    color: '#fff', border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '13px', fontWeight: '600',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    opacity: isSaving ? 0.7 : 1,
                                }}
                            >
                                {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="save" />}
                                {isSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Modal Confirmar Exclusão */}
            {showConfirmDeleteModal && (
                <>
                    <div
                        onClick={() => { setIsModalVisible(false); setTimeout(() => setShowConfirmDeleteModal(false), 300); }}
                        style={{
                            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
                            zIndex: 1040, opacity: isModalVisible ? 1 : 0, transition: 'opacity 0.3s',
                        }}
                    />
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: `translate(-50%, ${isModalVisible ? '-50%' : '-45%'})`,
                        width: '100%', maxWidth: '400px',
                        backgroundColor: '#fff', borderRadius: '16px',
                        zIndex: 1050, padding: '28px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        opacity: isModalVisible ? 1 : 0,
                        transition: 'all 0.3s ease',
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{
                                width: '56px', height: '56px', borderRadius: '50%',
                                backgroundColor: '#fef2f2', margin: '0 auto 12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <FontAwesomeIcon icon="trash" style={{ fontSize: '22px', color: '#ef4444' }} />
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                                Excluir Cupom
                            </h3>
                            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                Tem certeza que deseja excluir o cupom{' '}
                                <strong style={{ color: '#111827' }}>{couponToDelete?.codigo}</strong>?
                                <br />Esta ação não pode ser desfeita.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => { setIsModalVisible(false); setTimeout(() => setShowConfirmDeleteModal(false), 300); }}
                                disabled={isSaving}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '8px',
                                    backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb',
                                    color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '8px',
                                    backgroundColor: '#ef4444', border: 'none',
                                    color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '13px', fontWeight: '600',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                    opacity: isSaving ? 0.7 : 1,
                                }}
                            >
                                {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="trash" />}
                                {isSaving ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ─── Estilos reutilizáveis ────────────────────────────────────────────────────
const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: '#374151',
    display: 'block', marginBottom: '6px',
};

const inputStyle = {
    width: '100%', padding: '9px 12px',
    border: '1px solid #e5e7eb', borderRadius: '8px',
    fontSize: '13px', outline: 'none',
    backgroundColor: '#fff', color: '#374151',
    transition: 'border-color 0.15s',
};

// ─── Card de Cupom ────────────────────────────────────────────────────────────
const CouponCard = ({ coupon, canManage, onEdit, onToggle, onDelete }) => {
    const isExpired = coupon.data_validade && new Date(coupon.data_validade) < new Date();
    const usosMaximos = coupon.usos_maximos === -1 ? null : coupon.usos_maximos;
    const percentUsado = usosMaximos ? Math.min((coupon.usos_atuais / usosMaximos) * 100, 100) : 0;

    return (
        <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: `1px solid ${coupon.ativo && !isExpired ? '#e5e7eb' : '#f3f4f6'}`,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            opacity: coupon.ativo && !isExpired ? 1 : 0.7,
            transition: 'box-shadow 0.15s',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
            {/* Ícone */}
            <div style={{
                width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0,
                background: coupon.tipo_desconto === 'porcentagem'
                    ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                    : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <FontAwesomeIcon icon="tag" style={{ color: '#fff', fontSize: '18px' }} />
            </div>

            {/* Info principal */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                        fontSize: '14px', fontWeight: '700', color: '#111827',
                        fontFamily: 'monospace', letterSpacing: '0.5px',
                    }}>
                        {coupon.codigo}
                    </span>
                    <span style={{
                        fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '999px',
                        backgroundColor: coupon.ativo && !isExpired ? '#dcfce7' : '#f3f4f6',
                        color: coupon.ativo && !isExpired ? '#16a34a' : '#9ca3af',
                    }}>
                        {isExpired ? 'Expirado' : coupon.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {/* Desconto */}
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>
                        {coupon.tipo_desconto === 'porcentagem'
                            ? `${coupon.valor}% off`
                            : `R$ ${parseFloat(coupon.valor).toFixed(2)} off`
                        }
                    </span>

                    {/* Usos */}
                    <span style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FontAwesomeIcon icon="users" style={{ fontSize: '10px' }} />
                        {coupon.usos_atuais} {usosMaximos ? `/ ${usosMaximos}` : 'usos'}
                    </span>

                    {/* Validade */}
                    {coupon.data_validade && (
                        <span style={{ fontSize: '12px', color: isExpired ? '#ef4444' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FontAwesomeIcon icon="calendar" style={{ fontSize: '10px' }} />
                            {new Date(coupon.data_validade).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>

                {/* Barra de progresso de usos */}
                {usosMaximos && (
                    <div style={{ marginTop: '8px', height: '3px', backgroundColor: '#f3f4f6', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{
                            height: '100%', borderRadius: '999px',
                            backgroundColor: percentUsado >= 90 ? '#ef4444' : percentUsado >= 70 ? '#f59e0b' : '#22c55e',
                            width: `${percentUsado}%`, transition: 'width 0.3s',
                        }} />
                    </div>
                )}
            </div>

            {/* Ações */}
            {canManage && (
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                        onClick={onEdit}
                        title="Editar"
                        style={{
                            width: '32px', height: '32px', borderRadius: '8px',
                            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                            color: '#3b82f6', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        }}
                    >
                        <FontAwesomeIcon icon="edit" />
                    </button>
                    <button
                        onClick={onToggle}
                        title={coupon.ativo ? 'Inativar' : 'Ativar'}
                        style={{
                            width: '32px', height: '32px', borderRadius: '8px',
                            backgroundColor: coupon.ativo ? '#fef2f2' : '#f0fdf4',
                            border: `1px solid ${coupon.ativo ? '#fecaca' : '#bbf7d0'}`,
                            color: coupon.ativo ? '#ef4444' : '#22c55e',
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        }}
                    >
                        <FontAwesomeIcon icon={coupon.ativo ? 'toggle-on' : 'toggle-off'} />
                    </button>
                    <button
                        onClick={onDelete}
                        title="Excluir"
                        style={{
                            width: '32px', height: '32px', borderRadius: '8px',
                            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                            color: '#ef4444', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        }}
                    >
                        <FontAwesomeIcon icon="trash" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default CuponsTab;
